import { Form, Formik } from 'formik';
import keyBy from 'lodash/keyBy';
import noop from 'lodash/noop';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';

import { PurchasesResponse } from '../../api/fetchPurchases';
import { FormPagination } from '../../base-components/FormPagination';
import { metaTags } from '../_dummyData';
import useModalState from '../_hooks/useModalState';
import { FormQueryParams } from './components/FormQueryParams';
import { InstantSubmit } from './components/InstantSubmit';
import { PageHeader } from './components/PageHeader';
import { Customer } from './Customer/Customer';
import { Filter } from './Filter';
import { useOnChange } from './hooks/useOnChange';
import { useInitialState } from './hooks/useQueryParams';
import { useScrollToAnchor } from './hooks/useScrollToAnchor';
import { List } from './List';
import { PlannedNav } from './PlannedPurchases/PlannedNav';
import { PurchasesState } from './Purchases.type';
import styles from './PurchasesContainer.module.scss';
import { Stats } from './Stats';
import { SortByOption } from './types';

const sortByOptions: SortByOption[] = [
    {
        label: 'дате публикации извещения',
        value: 'publish_date',
    },
    {
        label: 'дате окончания приёма заявок',
        value: 'phase_receive_finish_ts',
    },
    {
        label: 'минимальной цене лота',
        value: 'min_lot_start_price',
    },
    {
        label: 'максимальной цене лота',
        value: 'max_lot_start_price',
    },
];

export interface PurchasesProps extends PurchasesResponse {}

export const PurchasesContainer: React.FC<PurchasesProps> = ({
    announces,
    orgSection,
    reference,
    total,
    matches,
    selectableOptions,
}) => {
    const router = useRouter();
    const { initialState, emptyFilterState } = useInitialState();
    const [state, setState] = useState<PurchasesState>(initialState);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isRegionsModalVisible, showRegionsModal, hideRegionsModal] =
        useModalState();
    const showOrgSection = Boolean(orgSection?.orgPublicProps);
    const showPlanned = useMemo(
        () => Boolean(state.sectionOrg),
        [state.sectionOrg]
    );

    /*
     * При пагинации выполняем прокрутку к началу страницы, после блока фильтров
     */
    const [scrollAnchor, scrollToAnchor] = useScrollToAnchor();
    useOnChange(state.page, scrollToAnchor);

    const regionsByCode = useMemo(
        () => keyBy(reference.regions, 'regionCode'),
        [reference.regions]
    );

    const heading = useMemo(() => {
        if (orgSection?.orgTitle) {
            return `Тендеры ${orgSection?.orgTitle}`;
        }

        if (typeof router.query.section === 'string') {
            return metaTags[router.query.section].headings;
        }

        return 'Поиск закупок';
    }, [orgSection?.orgTitle, router.query.section]);

    return (
        <div className={styles.container}>
            <PageHeader>{heading}</PageHeader>

            <Formik initialValues={state} onSubmit={noop}>
                <Form noValidate>
                    {showOrgSection && <Customer orgSection={orgSection} />}
                    {showPlanned && (
                        <PlannedNav orgTitle={orgSection?.orgTitle} />
                    )}
                    <Filter
                        reference={reference}
                        selectableOptions={selectableOptions}
                        matches={matches}
                        emptyFilterState={emptyFilterState}
                        isRegionsModalVisible={isRegionsModalVisible}
                        showRegionsModal={showRegionsModal}
                        hideRegionsModal={hideRegionsModal}
                    />

                    {scrollAnchor}

                    <Stats
                        referenceSortByOptions={sortByOptions}
                        total={total}
                        isLoading={isLoading}
                    />

                    <div
                        style={{
                            margin: '0 0 16px 0',
                            padding: '10px 10px',
                            fontSize: '22px',
                            textAlign: 'center',
                            color: '#2d9cdb',
                        }}
                    >
                        Уважаемые партнёры и участники закупочных процедур!{' '}
                        <br />
                        Информируем, что закупки ПАО «ПИК» СЗ теперь также
                        доступны на специализированной{' '}
                        <b>
                            <i>
                                <a href="https://comm.estp.ru/office/public/announces">
                                    платформе
                                </a>
                            </i>
                        </b>
                        .
                    </div>

                    <List announces={announces} regionsByCode={regionsByCode} />

                    <div className={styles.pagination}>
                        <FormPagination name="page" total={total} limit={10} />
                    </div>

                    <FormQueryParams
                        state={state}
                        emptyFilterState={emptyFilterState}
                        setLoading={setLoading}
                    />

                    <InstantSubmit onChange={setState} />
                </Form>
            </Formik>
        </div>
    );
};
